/**
 * Cash Flow Statement (BE-14a, indirect method)
 *
 * Operating, investing and financing activities for a period, then net change, opening and closing cash.
 * Banded styling like the P&L and Balance Sheet; an account line opens its journal entries.
 * Route: /reports/cash-flow
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, Skeleton } from '@mui/material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { PeriodSelector, ReportExportMenu } from '../../components/accounting';
import { formatDate, formatCurrency } from '../../utils/accounting/formatters';
import { getPeriodDates, toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage } from '../../utils/apiConvert';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { SectionHeader, LineItem, TotalLine, ResultBand } from './BalanceSheet';

const SECTIONS = [
  { key: 'operating', label: 'Operating Activities', total: 'operatingTotal', totalLabel: 'Net cash from operating activities' },
  { key: 'investing', label: 'Investing Activities', total: 'investingTotal', totalLabel: 'Net cash from investing activities' },
  { key: 'financing', label: 'Financing Activities', total: 'financingTotal', totalLabel: 'Net cash from financing activities' },
];

const CashFlow = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('THIS_MONTH');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = getPeriodDates(period);
      setData(await financeReportsService.getCashFlow({ from: toYmd(from), to: toYmd(to) }));
    } catch (err) {
      setError(serverMessage(err, 'Failed to load the cash flow statement'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const openAccount = (line) => (line.accountNumber
    ? () => navigate(`/accounting/journal-entries?account=${encodeURIComponent(line.accountNumber)}&to=${data.to}`)
    : undefined);
  const difference = Number(data?.difference || 0);

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Cash Flow Statement</Typography>
          {data && (
            <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
              {formatDate(data.from)} – {formatDate(data.to)} · indirect method
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <PeriodSelector value={period} onChange={setPeriod} />
          <ReportExportMenu reportType="cash-flow" params={{ from: data?.from, to: data?.to }} onError={setError} />
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {data && Math.abs(difference) >= 0.005 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          The statement does not tie: closing cash differs from opening cash plus the net change by {formatCurrency(difference)}.
        </Alert>
      )}

      {loading && !data && (
        <Box>{[...Array(8)].map((_, i) => <Skeleton key={i} height={28} />)}</Box>
      )}

      {data && (
        <Box>
          {SECTIONS.map((sec) => (
            <Box key={sec.key}>
              <SectionHeader>{sec.label}</SectionHeader>
              {(data[sec.key] || []).length === 0 && (
                <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', py: 0.4, px: 1.5, pl: 3.5 }}>No movement</Typography>
              )}
              {(data[sec.key] || []).map((line, i) => (
                <LineItem key={`${sec.key}-${line.accountNumber || line.label}-${i}`} label={line.label} amount={line.amount} onClick={openAccount(line)} />
              ))}
              <TotalLine label={sec.totalLabel} amount={data[sec.total]} />
            </Box>
          ))}

          <ResultBand label="Net Change in Cash" amount={data.netChange} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, px: 1.5 }}>
            <Typography sx={{ fontSize: fs.sm, color: 'text.secondary' }}>Opening cash ({formatDate(data.from)})</Typography>
            <Typography sx={{ fontSize: fs.sm }}>{formatCurrency(data.openingCash)}</Typography>
          </Box>
          <ResultBand label={`Closing Cash (${formatDate(data.to)})`} amount={data.closingCash} />
          <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mt: 1, px: 1.5 }}>
            Cash is every bank, cash drawer and e-wallet account. An entry and its reversal in the same period are left out.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CashFlow;
